declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA_ID = 'G-Y8PQ9JKW86';

function send(event: string, params?: Record<string, unknown>): void {
  if (typeof window?.gtag === 'function') {
    window.gtag('event', event, { ...params, send_to: GA_ID });
  }
}

// ─── Page / Symbol ──────────────────────────────────────────────────────────

export function trackPageView(symbol: string): void {
  if (typeof window?.gtag === 'function') {
    window.gtag('config', GA_ID, {
      page_title: `Chart — ${symbol}`,
      page_path: `/chart/${symbol}`,
    });
  }
}

export function trackSectorPageView(sectorName: string, slug: string): void {
  if (typeof window?.gtag === 'function') {
    window.gtag('config', GA_ID, {
      page_title: `${sectorName} — CeylonCharts`,
      page_path: `/sector/${slug}`,
    });
  }
}

export function trackSymbolChange(symbol: string, previousSymbol?: string): void {
  send('symbol_change', { symbol, previous_symbol: previousSymbol ?? null });
}

// ─── Timeframe ───────────────────────────────────────────────────────────────

export function trackTimeframeChange(timeframe: string): void {
  send('timeframe_change', { timeframe });
}

// ─── Watchlist ───────────────────────────────────────────────────────────────

export function trackWatchlistAction(action: 'add' | 'remove', symbol: string): void {
  send('watchlist_action', { action, symbol });
}

// ─── Symbol Search ───────────────────────────────────────────────────────────

export function trackSymbolSearch(query: string, resultsCount: number): void {
  send('symbol_search', { search_term: query, results_count: resultsCount });
}

// ─── Indicators ──────────────────────────────────────────────────────────────

export function trackIndicatorAction(action: 'add' | 'remove', indicatorName: string): void {
  send('indicator_action', { action, indicator_name: indicatorName });
}

// ─── Compare ─────────────────────────────────────────────────────────────────

export function trackCompareAction(action: 'add' | 'remove', symbol: string): void {
  send('compare_action', { action, symbol });
}

// ─── UI Actions ──────────────────────────────────────────────────────────────

export function trackUIAction(
  action: 'theme_toggle' | 'fullscreen' | 'screenshot' | 'reset_view' | 'share_chart',
  detail?: string,
): void {
  send('ui_action', { action, detail: detail ?? null });
}

// ─── Drawing Tools ───────────────────────────────────────────────────────────

export function trackDrawingTool(toolName: string): void {
  send('drawing_tool', { tool_name: toolName });
}

// ─── Saved Watchlists ───────────────────────────────────────────────────────────────

export function trackSavedWatchlistAction(
  action: 'save' | 'load' | 'delete',
  symbolCount?: number,
): void {
  send('saved_watchlist_action', { action, symbol_count: symbolCount ?? null });
}

// ─── Auth ───────────────────────────────────────────────────────────────────────

export function trackAuthAction(
  action: 'login' | 'logout' | 'register' | 'google_login',
  plan?: string,
): void {
  send('auth_action', { action, plan: plan ?? null });
}

// ─── Upgrade / Trial ─────────────────────────────────────────────────────────

export function trackUpgradeModalOpen(
  source: 'header' | 'watchlist' | 'indicator_template' | 'advanced_indicator' | 'other',
): void {
  send('upgrade_modal_open', { source });
}

export function trackNudgeShown(nudgeType: 'signin' | 'pro' | 'alerts' | 'screening' | 'financials'): void {
  send('nudge_shown', { nudge_type: nudgeType });
}

export function trackTrialStart(): void {
  send('trial_start');
}

export function trackUpgradeRequest(billing: 'monthly' | 'annual', duringTrial: boolean): void {
  send('upgrade_request', { billing, during_trial: duringTrial });
}

// ─── RS Lines ────────────────────────────────────────────────────────────────

export function trackRSLineAction(
  action: 'add' | 'remove' | 'add_defaults' | 'show_all' | 'hide_all',
  benchmarkSymbol?: string,
): void {
  send('rs_line_action', { action, benchmark_symbol: benchmarkSymbol ?? null });
}

// ─── Macro ───────────────────────────────────────────────────────────────────

export function trackMacroPickerOpen(source: 'header' | 'macro_bar'): void {
  send('macro_picker_open', { source });
}

export function trackMacroAction(
  action: 'pin' | 'unpin' | 'remove',
  seriesId: string,
): void {
  send('macro_action', { action, series_id: seriesId });
}

export function trackMacroVisibility(seriesId: string, visible: boolean): void {
  send('macro_visibility', { series_id: seriesId, visible });
}

export function trackMacroDetailOpen(seriesId: string, tab?: 'trend' | 'ccf' | 'zscore'): void {
  send('macro_detail_open', { series_id: seriesId, tab: tab ?? null });
}

export function trackMacroDetailTab(seriesId: string, tab: 'trend' | 'ccf' | 'zscore' | 'breakdown' | 'related'): void {
  send('macro_detail_tab', { series_id: seriesId, tab });
}

export function trackMacroAI(seriesId: string, symbol: string): void {
  send('macro_ai_interpretation', { series_id: seriesId, symbol });
}

// ─── Alerts ────────────────────────────────────────────────────────────────

type AlertAction = 'open_create' | 'open_edit' | 'create' | 'update' | 'delete' | 'refresh';
type AlertModalState = 'open' | 'close';
type AlertModalMode = 'create' | 'edit';

export function trackAlertAction(
  action: AlertAction,
  params?: {
    symbol?: string;
    conditionTypes?: string[];
    conditionCount?: number;
    source?: 'manual' | 'auto';
  },
): void {
  send('alert_action', {
    action,
    symbol: params?.symbol ?? null,
    condition_types: params?.conditionTypes?.join(',') ?? null,
    condition_count: params?.conditionCount ?? null,
    source: params?.source ?? null,
  });
}

export function trackAlertModal(
  state: AlertModalState,
  mode: AlertModalMode,
  symbol?: string,
): void {
  send('alert_modal', {
    state,
    mode,
    symbol: symbol ?? null,
  });
}
